import { PAID_SUBSCRIPTION_TIERS, type PaidSubscriptionTier } from '@pulsify/shared/dto'
import { useState } from 'react'
import { useTrialSubscription } from '~/entities/subscription/model/use-stripe'
import { planName } from '~/entities/subscription/plans'
import * as m from '~/paraglide/messages'
import { Button } from '~/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/shared/ui/dialog'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/shared/ui/select'

interface TrialSubscriptionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  businessId: string | undefined
}

export function TrialSubscriptionDialog({
  open,
  onOpenChange,
  businessId,
}: TrialSubscriptionDialogProps) {
  const [productType, setProductType] = useState<PaidSubscriptionTier>('basic')
  const [durationDays, setDurationDays] = useState(30)
  const trialSubscription = useTrialSubscription(businessId)

  const handleConfirm = () => {
    trialSubscription.mutate(
      { productType, durationDays },
      { onSuccess: () => onOpenChange(false) },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>{m.trialSubscriptionConfirmTitle()}</DialogTitle>
          <DialogDescription>
            {m.trialSubscriptionConfirmDescription({
              durationDays,
              productType: planName(productType),
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>{m.productType()}</Label>
            <Select
              value={productType}
              onValueChange={(v) => setProductType(v as PaidSubscriptionTier)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAID_SUBSCRIPTION_TIERS.map((tier) => (
                  <SelectItem key={tier} value={tier}>
                    {planName(tier)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label>{m.durationDays()}</Label>
            <Input
              type="number"
              min={1}
              value={durationDays}
              onChange={(e) =>
                setDurationDays(Math.max(1, Number.parseInt(e.target.value, 10) || 1))
              }
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={trialSubscription.isPending}
          >
            {m.cancel()}
          </Button>
          <Button onClick={handleConfirm} disabled={trialSubscription.isPending}>
            {m.activateTrial()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
